import type {
  DecideMemberMarketingConsentRequest,
  MemberMarketingOperationResult,
  MemberMarketingWorkspace,
  PublicMemberMarketingUnsubscribeRequest,
  PublicMemberMarketingUnsubscribeResult,
  QueueMemberMarketingMessageRequest,
} from "@billiard/contracts";
import {
  isDecideMemberMarketingConsentRequest,
  isMemberMarketingOperationResult,
  isMemberMarketingWorkspace,
  isPublicMemberMarketingUnsubscribeRequest,
  isPublicMemberMarketingUnsubscribeResult,
  isQueueMemberMarketingMessageRequest,
} from "@billiard/contracts";
import { ApiRequestError, requestOperatorJson } from "./api";

export interface MemberMarketingApiContext {
  tenantId: string;
  storeId: string;
  accountId: string;
}

export type MemberMarketingMutationCommand =
  | {
      context: MemberMarketingApiContext;
      memberId: string;
      operation: "DECIDE_CONSENT";
      request: DecideMemberMarketingConsentRequest;
    }
  | {
      context: MemberMarketingApiContext;
      memberId: string;
      operation: "QUEUE_MESSAGE";
      request: QueueMemberMarketingMessageRequest;
    };

const STABLE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;

function contextIsValid(context: MemberMarketingApiContext): boolean {
  return [context.tenantId, context.storeId, context.accountId].every((value) =>
    STABLE_ID.test(value),
  );
}

function assertWorkspace(
  value: unknown,
  context: MemberMarketingApiContext,
  memberId: string,
): MemberMarketingWorkspace {
  if (
    !contextIsValid(context) ||
    !STABLE_ID.test(memberId) ||
    !isMemberMarketingWorkspace(value) ||
    value.tenantId !== context.tenantId ||
    value.storeId !== context.storeId ||
    value.memberId !== memberId
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "会员营销资料与当前账号、门店或会员不一致，系统已停止显示",
    );
  }
  return value;
}

export function memberMarketingCommandIsValid(
  command: unknown,
): command is MemberMarketingMutationCommand {
  if (
    typeof command !== "object" ||
    command === null ||
    !("context" in command) ||
    !("memberId" in command) ||
    !("operation" in command) ||
    !("request" in command)
  ) {
    return false;
  }
  const candidate = command as MemberMarketingMutationCommand;
  return (
    contextIsValid(candidate.context) &&
    STABLE_ID.test(candidate.memberId) &&
    (candidate.operation === "DECIDE_CONSENT"
      ? isDecideMemberMarketingConsentRequest(candidate.request)
      : candidate.operation === "QUEUE_MESSAGE" &&
        isQueueMemberMarketingMessageRequest(candidate.request))
  );
}

function assertResult(
  value: unknown,
  command: MemberMarketingMutationCommand,
): MemberMarketingOperationResult {
  const requestMatches = (() => {
    if (!isMemberMarketingOperationResult(value)) return false;
    if (command.operation === "QUEUE_MESSAGE") {
      return (
        value.message !== null &&
        value.message.channel === command.request.channel &&
        value.message.subject === command.request.subject &&
        value.message.body === command.request.body &&
        value.message.consentVersion === command.request.expectedConsentVersion &&
        value.workspace.messages.some(
          (message) =>
            message.messageId === value.message?.messageId &&
            JSON.stringify(message) === JSON.stringify(value.message),
        )
      );
    }
    const consent = value.workspace.consents.find(
      (candidate) => candidate.channel === command.request.channel,
    );
    return (
      value.message === null &&
      consent?.version === command.request.expectedConsentVersion + 1 &&
      consent.state === (command.request.decision === "GRANT" ? "GRANTED" : "OPTED_OUT") &&
      consent.frequency === command.request.frequency &&
      consent.sourceDetail === command.request.evidenceNote
    );
  })();
  if (
    !isMemberMarketingOperationResult(value) ||
    !requestMatches ||
    value.operation !== command.operation ||
    value.workspace.tenantId !== command.context.tenantId ||
    value.workspace.storeId !== command.context.storeId ||
    value.workspace.memberId !== command.memberId
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "营销操作回执与原账号、门店、会员或操作不一致，结果暂时无法确认",
    );
  }
  return value;
}

export async function getMemberMarketingWorkspace(
  context: MemberMarketingApiContext,
  memberId: string,
  signal?: AbortSignal,
): Promise<MemberMarketingWorkspace> {
  const value = await requestOperatorJson<unknown>(
    `/v1/members/${encodeURIComponent(memberId)}/marketing`,
    { cache: "no-store", ...(signal ? { signal } : {}) },
  );
  return assertWorkspace(value, context, memberId);
}

export async function executeMemberMarketingCommand(
  command: MemberMarketingMutationCommand,
): Promise<MemberMarketingOperationResult> {
  if (!memberMarketingCommandIsValid(command)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "营销原请求不完整，系统没有发送或重试",
      400,
    );
  }
  const suffix = command.operation === "DECIDE_CONSENT" ? "consent" : "messages";
  const value = await requestOperatorJson<unknown>(
    `/v1/members/${encodeURIComponent(command.memberId)}/marketing/${suffix}`,
    { method: "POST", cache: "no-store", body: JSON.stringify(command.request) },
  );
  return assertResult(value, command);
}

export async function getMemberMarketingOperationByRequest(
  command: MemberMarketingMutationCommand,
  signal?: AbortSignal,
): Promise<MemberMarketingOperationResult | null> {
  if (!memberMarketingCommandIsValid(command)) {
    throw new ApiRequestError("REQUEST_VALIDATION_FAILED", "营销原请求不完整，系统没有查询", 400);
  }
  try {
    const value = await requestOperatorJson<unknown>(
      `/v1/members/${encodeURIComponent(command.memberId)}/marketing/by-request/${command.operation}/${encodeURIComponent(command.request.idempotencyKey)}`,
      { cache: "no-store", ...(signal ? { signal } : {}) },
    );
    return assertResult(value, command);
  } catch (error) {
    if (
      error instanceof ApiRequestError &&
      error.statusCode === 404 &&
      error.code === "MEMBER_MARKETING_OPERATION_NOT_FOUND"
    ) {
      return null;
    }
    throw error;
  }
}

export async function unsubscribeMemberMarketing(
  request: PublicMemberMarketingUnsubscribeRequest,
): Promise<PublicMemberMarketingUnsubscribeResult> {
  if (!isPublicMemberMarketingUnsubscribeRequest(request)) {
    throw new ApiRequestError("REQUEST_VALIDATION_FAILED", "退订链接不完整，系统没有提交", 400);
  }
  const value = await requestOperatorJson<unknown>("/v1/public/member-marketing/unsubscribe", {
    method: "POST",
    cache: "no-store",
    body: JSON.stringify(request),
  });
  if (!isPublicMemberMarketingUnsubscribeResult(value)) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "退订结果无法安全核对，请保留当前页面并联系门店",
    );
  }
  return value;
}

export function memberMarketingOutcomeIsUnknown(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  return (
    error.code === "GATEWAY_UNREACHABLE" ||
    error.code === "GATEWAY_RESPONSE_INVALID" ||
    error.code === "OPERATOR_IDENTITY_CHANGED" ||
    (error.statusCode !== undefined && error.statusCode >= 500)
  );
}
